import { getTime, subDays } from 'date-fns';
import { PAGINATION_LIMIT } from '@saucelabs/sl-util-config';
import { actions as authActions } from '@saucelabs/sl-container-authentication';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { actions as modalActions } from '@saucelabs/sl-container-modal';
import { actions as userActions } from '@saucelabs/sl-module-user';
import { helpers as tableHelpers } from '@saucelabs/sl-component-tables';

import * as actions from './actions';
import * as api from './api';
import { TABLE_DATA_PERIOD } from './../activity/constants';
import { actions as selectedUserActions } from './../selected-users';
import { operations as managementOperations, utils as tmUtils } from './../team-management';
import { actions as filtersActions } from './../filters';
import { USER_TABLE } from '../../constants';
import { actions as userDetailsActions } from './../user-details';

export const getAllUserListData = ({ offset, allFilters }) => (dispatch, getState) => {
  const ordering = tableHelpers.serializeSorting(getState().table?.sorting?.[USER_TABLE.TABLE_NAME]).join(',') || undefined;
  dispatch(actions.getUserList({ offset, ordering, ...allFilters }))
    .then(({ data }) => {
      const userIds = data.results.map((user) => user.id);
      dispatch(userActions.getUserListActivity({
        users: userIds,
        since: Math.round(getTime(subDays(new Date(), TABLE_DATA_PERIOD)) / 1000), // API expects seconds instead of miliseconds
      }));
      dispatch(userActions.getUserListLastJobs({ users: userIds }));
    });
};

export const updateTableOffset = (payload) => (dispatch, getState) => {
  const offset = (payload - 1) * PAGINATION_LIMIT;
  const allFilters = tmUtils.serializeFilters(getState().apps.teamManagement.users.get('selectedFilters'));
  dispatch(actions.saveTableOffset(payload));
  dispatch(getAllUserListData({ offset, allFilters }));
};

export const toggleFilter = (payload) => (dispatch, getState) => {
  const filters = tmUtils.updateFilter(
    getState().apps.teamManagement.users.getIn(['selectedFilters', payload.label]),
    payload.value,
  );

  dispatch(actions.updateFilter({ label: payload.label, value: filters }));
  dispatch(updateTableOffset(1)); // reset pagination to first page and refetch data
};

export const patchUser = (payload) => (dispatch) => {
  dispatch(actions.patchUserRequest());
  return api.patchUser(payload)
    .then(({ data }) => dispatch(actions.patchUserSuccess(data)))
    .catch((error) => {
      dispatch(actions.patchUserFailure(error));
      throw error;
    });
};

export const patchOrganization = (payload) => (dispatch) => {
  dispatch(actions.patchOrganizationRequest());
  return api.patchOrganization(payload)
    .then(() => {
      dispatch(authActions.getMeUserService());
      dispatch(actions.patchOrganizationSuccess());
      dispatch(notificationActions.notifySuccess('Organization updated'));
    })
    .catch((error) => {
      dispatch(actions.patchOrganizationFailure(error));
      throw error;
    });
};

export const removeMembership = (payload) => (dispatch, getState) => {
  dispatch(actions.removeMembershipRequest());
  const organization = getState().user.get('organization').id;
  return api.removeMembership({ ...payload, organization })
    .then(() => dispatch(actions.removeMembershipSuccess(payload)))
    .catch((error) => {
      dispatch(actions.removeMembershipFailure(error));
      throw error;
    });
};

export const batchRemoveMembership = () => (dispatch, getState) => {
  const { selectedUsers } = getState().apps.teamManagement;
  const selectedTeam = getState().apps.teamManagement.teamDetails.get('team');
  if (!selectedUsers.size) {
    return dispatch(notificationActions.notifyWarning('You need to select users from table first'));
  }

  const changeRoleRequests = selectedUsers
    .map((user) => dispatch(removeMembership({ user: user.id, team: selectedTeam.id })))
    .toArray();

  // FIXME: it will work for 2-4 changes at once, but wont for eg 100
  return Promise.all(changeRoleRequests).then(
    () => {
      dispatch(notificationActions.notifySuccess('User removed from team successfully'));
      dispatch(selectedUserActions.clearUsersSelection());
      dispatch(filtersActions.getFilters({ team: selectedTeam.id }));
    },
    () => dispatch(notificationActions.notifyDanger('There was problem with membership removal')),
  );
};

const refetchUserList = (payload) => (dispatch, getState) => {
  const { teamId } = payload;
  const allFilters = tmUtils.serializeFilters(getState().apps.teamManagement.users.get('selectedFilters'));
  const offset = (getState().apps.teamManagement.users.getIn(['users', 'currentPage']) - 1) * PAGINATION_LIMIT; // TODO refactor to use selector

  return Promise.all([
    dispatch(selectedUserActions.clearUsersSelection()),
    dispatch(filtersActions.getFilters(teamId ? { team: teamId } : undefined)),
    dispatch(getAllUserListData({ offset, allFilters })),
  ]);
};

export const deactivateUser = (payload) => (dispatch) => {
  dispatch(actions.deactivateUserRequest(payload));
  return api.deactivateUser(payload)
    .then(() => {
      dispatch(selectedUserActions.toggleUserSelection(payload));
      return dispatch(actions.deactivateUserSuccess(payload));
    })
    .catch((error) => {
      dispatch(actions.deactivateUserFailure(error));
      throw error;
    });
};

export const batchDeactivateUsers = (payload, teamId) => (dispatch) => {
  if (!payload.size) {
    return dispatch(notificationActions.notifyWarning('You need to select users from table first'));
  }

  const deactivateUserRequests = payload
    .map((user) => dispatch(deactivateUser(user.id)))
    .toArray();

  // FIXME: it will work for 2-4 changes at once, but wont for eg 100
  return Promise.all(deactivateUserRequests).then(
    () => {
      dispatch(notificationActions.notifySuccess('Users deactivated'));
      dispatch(modalActions.hideModal());
    },
    () => dispatch(notificationActions.notifyDanger('Something went wrong with deactivating users')),
  ).then(() => dispatch(refetchUserList({ teamId })));
};

export const reactivateUser = (payload) => (dispatch) => {
  dispatch(actions.reactivateUserRequest(payload));
  return api.reactivateUser(payload)
    .then(() => {
      dispatch(selectedUserActions.toggleUserSelection(payload));
      return dispatch(actions.reactivateUserSuccess(payload));
    })
    .catch((error) => {
      dispatch(actions.reactivateUserFailure(error));
      throw error;
    });
};

export const batchReactivateUsers = (payload, teamId) => (dispatch) => {
  if (!payload.size) {
    return dispatch(notificationActions.notifyWarning('You need to select users from table first'));
  }

  const reactivateUserRequests = payload
    .map((user) => dispatch(reactivateUser(user.id)))
    .toArray();

  // FIXME: it will work for 2-4 changes at once, but wont for eg 100
  return Promise.all(reactivateUserRequests).then(
    () => {
      dispatch(notificationActions.notifySuccess('Users reactivated'));
      dispatch(modalActions.hideModal());
    },
    () => dispatch(notificationActions.notifyDanger('Something went wrong with reactivating users')),
  ).then(() => dispatch(refetchUserList({ teamId })));
};

export const updateRole = (payload) => (dispatch) => {
  dispatch(actions.updateRoleRequest(payload));

  const apiCall = {
    admin: api.grantOrgAdmin,
    member: api.demoteToMember,
    team_admin: api.grantTeamAdmin,
  }[payload.role] || (() => Promise.reject(new Error('Role not implemented')));

  return apiCall(payload.userId)
    .then(({ data }) => dispatch(actions.updateRoleSuccess(data)))
    .catch((error) => {
      dispatch(actions.updateRoleFailure(error));
      throw error;
    });
};

export const batchUpdateRoles = (payload) => (dispatch, getState) => {
  const { selectedUsers } = getState().apps.teamManagement;
  if (!selectedUsers.size) {
    return dispatch(notificationActions.notifyWarning('You need to select users from table first'));
  }

  const changeRoleRequests = selectedUsers.map((user) => (
    dispatch(updateRole({ userId: user.id, role: payload }))
      .catch((error) => {
        const text = (error.response && error.response.data && error.response.data.detail) ||
          'Something went wrong with roles change';
        dispatch(notificationActions.notifyDanger(text));
      })
  )).toArray();

  // FIXME: it will work for 2-4 changes at once, but wont for eg 100
  return Promise.all(changeRoleRequests).then(
    (responses) => {
      const successCount = responses.reduce((acc, elem) => acc + (elem ? 1 : 0), 0);
      if (successCount) {
        dispatch(notificationActions.notifySuccess(`Changed roles for ${successCount} users`));
      }
      dispatch(selectedUserActions.clearUsersSelection());
      dispatch(filtersActions.getFilters());
    },
  );
};

export const changeAssignedTeams = (payload) => (dispatch) => {
  // Wrapper around old flow and new flow.
  // If teams are passed, we known that user is in org with multi team per user access
  // In other cases team will be passed, and we will call legacy endpoints.
  const { userId, teamId, organizationId, teams } = payload;

  if (teams) {
    return dispatch(userDetailsActions.changeAssignedTeams({ userId, teams }));
  }

  return dispatch(actions.teamAssignment({ userId, teamId, organizationId }));
};

export const batchTeamAssignment = ({ users, teamId, teams }) => (dispatch, getState) => {
  const organizationId = getState().user.get('organization').id;
  const teamAssignmentRequests = users
    .map((userId) => dispatch(changeAssignedTeams({ userId, teamId, organizationId, teams })))
    .toArray();

  // FIXME: it will work for 2-4 changes at once, but wont for eg 100
  return Promise.all(teamAssignmentRequests).then(
    () => {
      dispatch(notificationActions.notifySuccess('Users attached into team'));
      dispatch(selectedUserActions.clearUsersSelection());
      dispatch(modalActions.hideModal());
      // TODO possible refactor to use reducer instead of additional fetch
      dispatch(managementOperations.getInitialUserTable());
    },
    () => dispatch(notificationActions.notifyDanger('Something went wrong with team assignment')),
  );
};
